import type { CheckboxListItem } from '../types/checkbox-list-item.type';

export type CheckboxState = 'checked' | 'indeterminate' | 'unchecked';

export const collectCheckboxListIds = <I extends string>(
  items: readonly CheckboxListItem<I>[],
): readonly I[] =>
  items.flatMap((item) => [
    item.id,
    ...collectCheckboxListIds(item.children ?? []),
  ]);

const computeIdsState = <I extends string>(
  ids: readonly I[],
  value: readonly I[],
): CheckboxState => {
  const checkedCount = ids.filter((id) => value.includes(id)).length;

  if (!checkedCount) {
    return 'unchecked';
  }

  return checkedCount === ids.length ? 'checked' : 'indeterminate';
};

// An item stands for itself and its whole subtree: it is indeterminate as
// soon as part of the subtree only is checked.
export const computeCheckboxListItemState = <I extends string>(
  item: CheckboxListItem<I>,
  value: readonly I[],
): CheckboxState => computeIdsState(collectCheckboxListIds([item]), value);

const collectToggleableIds = <I extends string>(
  items: readonly CheckboxListItem<I>[],
): readonly I[] =>
  items
    .filter((item) => !item.disabled)
    .flatMap((item) => [item.id, ...collectToggleableIds(item.children ?? [])]);

export const hasCheckboxListToggleableItems = <I extends string>(
  items: readonly CheckboxListItem<I>[],
): boolean => collectToggleableIds(items).length > 0;

// "Sélectionner tout" reflects the items it can toggle, or every item when none can be (a fully
// locked list, then disabled).
export const computeCheckboxListAllState = <I extends string>(
  items: readonly CheckboxListItem<I>[],
  value: readonly I[],
): CheckboxState =>
  computeIdsState(
    hasCheckboxListToggleableItems(items)
      ? collectToggleableIds(items)
      : collectCheckboxListIds(items),
    value,
  );

// Checking an unchecked or indeterminate item checks its whole subtree,
// checking a checked item unchecks its whole subtree; the disabled items and
// the ids outside the list keep their state.
export const toggleCheckboxListItems = <I extends string>(
  items: readonly CheckboxListItem<I>[],
  value: readonly I[],
): readonly I[] => {
  const ids = collectToggleableIds(items);
  const valueWithoutIds = value.filter((id) => !ids.includes(id));

  return computeIdsState(ids, value) === 'checked'
    ? valueWithoutIds
    : [...valueWithoutIds, ...ids];
};
